import { T } from "@/i18n";
import { Eye, Fullscreen, Pencil, Trash } from "lucide-react";
import { CSSProperties, Key, ReactNode, useMemo } from "react";
import { DeletePrompt } from "../delete-prompt";
import { NavActions, NavItems } from "../nav-actions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { cn } from "@/lib/utils";
import { TableCell, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";

type Content = {
  value: ReactNode;
  label?: string;
  tableClassName?: string;
  tableZoom?: boolean;
};
interface DomainListItemCardProps {
  className?: string;
  style?: CSSProperties;
  domain: string;
  title: string | ReactNode;
  titleTableOptions?: Pick<Content, "tableClassName" | "tableZoom">;
  description?: string | ReactNode;
  descriptionTableOptions?: Pick<Content, "tableClassName" | "tableZoom">;
  contentMap: Array<Content | null>;
  children?: React.ReactNode;
  viewLink?: (children: ReactNode, key: Key) => JSX.Element;
  editLink?: (children: ReactNode, key: Key) => JSX.Element;
  onDelete?: () => void | Promise<void>;
  extraItems?: NavItems[number];
  table?: boolean;
  rowClassName?: string;
  rowStyle?: CSSProperties;
}
export function DomainListItemCard({
  className,
  style,
  title,
  titleTableOptions,
  description,
  descriptionTableOptions,
  contentMap,
  domain,
  children,
  editLink,
  viewLink,
  onDelete,
  extraItems,
  table = false,
  rowClassName,
  rowStyle,
}: DomainListItemCardProps) {
  const items = useMemo<NavItems[number]>(() => {
    const arr: NavItems[number] = [];
    if (viewLink) {
      arr.push({
        label: `${domain}.contextMenu.view`,
        icon: () => <Eye />,
        itemWrapper: ({ children, key }) => viewLink(children, key as Key),
      });
    }

    if (editLink) {
      arr.push({
        label: `${domain}.contextMenu.edit`,
        icon: () => <Pencil />,
        itemWrapper: ({ children, key }) => editLink(children, key as Key),
      });
    }
    if (extraItems) {
      arr.push(...extraItems);
    }
    return arr;
  }, [domain, editLink, extraItems, viewLink]);

  const secondItems = useMemo<NavItems[number]>(() => {
    const arr: NavItems[number] = [];
    if (onDelete) {
      arr.push({
        label: `${domain}.contextMenu.delete`,
        icon: () => <Trash />,
        className: "text-destructive",
        itemWrapper: ({ children, key }) => (
          <DeletePrompt
            key={key as Key}
            onProceed={onDelete}
            translations={{
              title: `${domain}.deletePrompt.title`,
              description: `${domain}.deletePrompt.description`,
              continue: `${domain}.deletePrompt.proceed`,
              cancel: `${domain}.deletePrompt.cancel`,
            }}
          >
            {children}
          </DeletePrompt>
        ),
      });
    }

    return arr;
  }, [domain, onDelete]);

  const tableExtraItems = useMemo(() => {
    const arr: NavItems[number] = [];
    if (extraItems) {
      arr.push(...extraItems);
    }
    arr.push(...secondItems);
    return arr;
  }, [extraItems, secondItems]);

  const descriptionValue = useMemo(
    () =>
      typeof description === "string" ? <T tKey={description} /> : description,
    [description],
  );

  if (table) {
    return (
      <TableRow className={cn(rowClassName)} style={rowStyle}>
        <TableCell className={cn(titleTableOptions?.tableClassName)}>
          {titleTableOptions?.tableZoom ? (
            <TableValueZoom>{title}</TableValueZoom>
          ) : (
            title
          )}
        </TableCell>
        {description !== null && description !== undefined && (
          <TableCell className={cn(descriptionTableOptions?.tableClassName)}>
            {descriptionTableOptions?.tableZoom ? (
              <TableValueZoom>{descriptionValue}</TableValueZoom>
            ) : (
              descriptionValue
            )}
          </TableCell>
        )}
        {contentMap.map((content, i) => (
          <TableCell key={i} className={cn(content?.tableClassName)}>
            {content?.tableZoom ? (
              <TableValueZoom>{content.value}</TableValueZoom>
            ) : (
              content?.value
            )}
          </TableCell>
        ))}
        {items.length ? (
          <TableCell className="flex h-[57px] items-center gap-4">
            {viewLink ? (
              viewLink(<Eye width={16} height={16} />, `table-view`)
            ) : (
              <></>
            )}
            {editLink ? (
              editLink(<Pencil width={16} height={16} />, `table-edit`)
            ) : (
              <></>
            )}
            {tableExtraItems.length === 0 ? (
              <></>
            ) : tableExtraItems.length > 1 ? (
              <NavActions items={[tableExtraItems]} />
            ) : (
              <div
                className={cn(
                  tableExtraItems[0].className,
                  "cursor-pointer [&>*]:h-4 [&>*]:w-4",
                )}
              >
                {tableExtraItems[0].itemWrapper?.({
                  children: tableExtraItems[0].icon(),
                  key: `table-${tableExtraItems[0].label}`,
                }) ?? tableExtraItems[0].icon()}
              </div>
            )}
          </TableCell>
        ) : (
          <></>
        )}
        {children}
      </TableRow>
    );
  }

  return (
    <Card className={cn("border-primary/35", className)} style={style}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <NavActions items={[items, secondItems]} />
        </div>
        <CardDescription>{descriptionValue}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1">
        {(contentMap.filter(Boolean) as Array<Content>).map((content, i) => (
          <span className="break-all pl-1 text-muted-foreground" key={i}>
            {content.label ? (
              <span className="font-medium text-card-foreground">
                {content.label}:{" "}
              </span>
            ) : (
              ""
            )}
            {content.value}
          </span>
        ))}
        {children}
      </CardContent>
    </Card>
  );
}

function TableValueZoom({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="text" className="mr-2">
            <Fullscreen />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <div className="grid">{children}</div>
        </DialogContent>
      </Dialog>
      {children}
    </>
  );
}
